import i18n from "@/i18n";
import { compactAbove1k, formatCount } from "@/lib/format";
import type { NodeId, NodeSpec } from "./nodeSpec";
import { NODES } from "./nodeSpec";
import type {
  BarPayload,
  KvRow,
  RendererPayload,
  RowPayload,
  TilePayload,
} from "./renderers";

type AnyRecord = Record<string, unknown>;

function asRecord(v: unknown): AnyRecord | undefined {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as AnyRecord) : undefined;
}

function asArray(v: unknown): unknown[] | undefined {
  return Array.isArray(v) ? v : undefined;
}

function asString(v: unknown, fallback = ""): string {
  return v == null ? fallback : String(v);
}

function asNumber(v: unknown): number | undefined {
  if (v == null) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

function fmtInt(v: unknown): string {
  const n = asNumber(v);
  return n == null ? "—" : formatCount(n);
}

function fmtBrl(v: unknown, opts: { currency?: boolean } = { currency: true }): string {
  const n = asNumber(v);
  if (n == null) return "—";
  const formatted = formatCount(n);
  return opts.currency ? `R$ ${formatted}` : formatted;
}

// Dinheiro, não token: abaixo de mil o valor exato importa (`R$ 847`), então
// aqui não vale o `<1k` de `compactTokens`. Compartilhado é só o limiar —
// `compactAbove1k` é quem sabe quando `k` viraria quatro dígitos.
function fmtBrlCompact(v: unknown): string {
  const n = asNumber(v);
  if (n == null) return "—";
  if (n < 1_000) return `R$ ${n}`;
  return `R$ ${compactAbove1k(n)}`;
}

/**
 * Severidade como fração da base faturável, não como contagem.
 *
 * O limiar absoluto (`>= 100` crítico, `>= 30` alerta) invertia o ranking
 * visual justamente nos dois extremos onde importa: numa base de 200 usuários,
 * 30 é 15% e saía `warn`; numa de 20.000, 100 é 0,5% e saía `crit`.
 *
 * Compartilhada pelo nó 02 (tcodes não classificados) e pelo nó 03 (módulos sem
 * tradução), com a base entrando por parâmetro — `scope_mapping` não carrega
 * `total_active`, então quem chama passa o do `classification`.
 *
 * Sem base não há fração, e `info` ali significa "não medi", não "é baixo".
 */
function severityForUserShare(
  userCount: number | undefined,
  totalActive: number | undefined,
): RowPayload["sev"] {
  if (userCount == null || totalActive == null || totalActive <= 0) return "info";
  const share = userCount / totalActive;
  if (share >= 0.1) return "crit";
  if (share >= 0.03) return "warn";
  return "info";
}

const MODULE_COLORS: Record<string, string> = {
  FI: "#08254c",
  MM: "#1d4ea0",
  SD: "#ffc83d",
  HCM: "#27a155",
  PP: "#f27325",
  CO: "#8a6200",
  BASIS: "#6d4c41",
  CRM: "#9c27b0",
};

const DEFAULT_MODULE_COLORS = ["#08254c", "#1d4ea0", "#ffc83d", "#27a155", "#f27325", "#8a6200", "#6d4c41", "#9c27b0"];

/**
 * Resultado vazio de um produtor que RODOU (#846).
 *
 * Diferente do `placeholder`, que afirma ausência de produção. Os dois usam a
 * mesma caixa; o que muda é a frase, e é a frase que estava mentindo: nó
 * concluído com lista vazia dizia "Awaiting Node 0X output".
 *
 * Observado na execução `008a9238` em 14/09/2026, com os 7 nós DONE:
 * `custom_tcodes_detail` e `ambiguous_users` existiam no payload como arrays de
 * tamanho zero, e as duas abas diziam que estavam esperando o nó.
 *
 * Zero ali é resposta, e boa: nenhum tcode Z/Y customizado, nenhum usuário
 * ambíguo. Mesmo vocabulário da #827, que separou "vazio" de "não medido" no
 * Diagnóstico — aqui na direção oposta.
 */
function emptyResult(
  kind: RendererPayload["kind"],
  title: string,
  desc: string,
): RendererPayload {
  return placeholder(kind, title, desc);
}

function placeholder(
  kind: RendererPayload["kind"],
  title: string,
  desc: string,
): RendererPayload {
  switch (kind) {
    case "kv":
      return { kind, title, desc, placeholder: true, rows: [] };
    case "tiles":
    case "tiles+table":
      return { kind, title, desc, placeholder: true, tiles: [] };
    case "bars":
      return { kind, title, desc, placeholder: true, bars: [] };
    case "rows":
      return { kind, title, desc, placeholder: true, rows: [] };
    case "logs":
      return { kind, title, desc, placeholder: true, lines: [] };
    case "table":
      return { kind, title, desc, placeholder: true, table: { cols: [], rows: [] } };
    case "note+table":
      return { kind, title, desc, placeholder: true, table: { cols: [], rows: [] } };
    case "markdown":
      return { kind, title, desc, placeholder: true, md: "" };
  }
}

// ── Per-node builders ──────────────────────────────────────────────────────
// Each takes the relevant `outputs` slice and returns a payload or null.
// Null becomes a placeholder rendered by NodeData.

// Nível 3 da cascata do connector: papel → tcode, contagem de papel como
// proxy. É o único nível que não mede execução — "pode rodar", não "rodou".
// Os níveis 1 e 2 (SWNC, SAPWL) contam execução que aconteceu, e chamá-los de
// "autorização" era menos verdadeiro que o tile neutro.
const USAGE_AUTH_SOURCE = "AGR_TCODES";

// A cascata inteira falhou. É ausência de medição, não procedência: não vai
// para a tela como se fosse o nome de uma fonte.
const USAGE_SOURCE_UNKNOWN = "unknown";

// O mesmo travessão do Diagnóstico para contador sem número conhecido.
const NO_USAGE_VALUE = "—";

function buildExtractPreview(extraction: AnyRecord | undefined): RendererPayload | null {
  if (!extraction) return null;
  const rawUsageSource =
    extraction.usage_data_source == null ? "" : String(extraction.usage_data_source);
  // Truthiness, e não `!= null`: o connector emite "" em campo não extraído, e
  // "unknown" é a cascata sem resposta. Nenhum dos dois é nome de fonte.
  const usageSource =
    rawUsageSource && rawUsageSource !== USAGE_SOURCE_UNKNOWN ? rawUsageSource : undefined;
  const usageWindow = asNumber(extraction.usage_window_months);
  const usageCoveredDays = asNumber(extraction.usage_covered_days);
  const usageIsAuthorization = usageSource === USAGE_AUTH_SOURCE;
  // Houve medição, ou só resposta? (#649) O `usage_measured` é o veredito que o
  // nó 01 publica; `"unknown"` cobre a execução persistida antes dele, porque
  // aquele valor só existe quando nível nenhum venceu a cascata. String vazia
  // fica de fora: ali o campo não foi extraído, e não saber não é saber que não.
  const usageNotMeasured =
    extraction.usage_measured === false || rawUsageSource === USAGE_SOURCE_UNKNOWN;
  // Veredito do backend (`per_user_usage_unavailable`): a regra completa é
  // "fonte ≠ SWNC **e** `usage_by_user` vazio", e reimplementá-la aqui foi
  // como as duas metades divergiram. Execuções persistidas antes do campo não
  // o têm, e para elas o tile não afirma nada sobre uso por usuário.
  const perUserUsageUnavailable = extraction.per_user_usage_unavailable === true;
  const usageParts: string[] = [];
  // A janela é propriedade de uma medição dentro dela, e sem fonte não houve
  // medição. Exigir `usageSource` cobre de uma vez os três estados que
  // execuções persistidas antes do fix do backend deixaram no banco: pull que
  // falhou (`usage_data_source: null`), cascata sem resposta (`"unknown"`) e
  // `AGR_TCODES`, que não tem dimensão de tempo. Com só o teste de autorização
  // aqui, os dois primeiros voltavam a imprimir "36-month window" sobre zero.
  //
  // E exigir `usageCoveredDays` (#649): o `usage_window_months` carrega a janela
  // **pedida** quando o connector não declara cobertura, e imprimir
  // "36-month window" sobre a leitura de uma âncora única é a mesma frase que a
  // #646 apagou do Diagnóstico, um degrau ao lado. Abaixo de um mês o backend
  // não emite meses, e os dias são o único número verdadeiro.
  if (usageSource && !usageIsAuthorization && !usageNotMeasured && usageCoveredDays != null) {
    usageParts.push(
      usageWindow != null
        ? i18n.t("{{count}}-month window", { count: usageWindow })
        : i18n.t("{{count}}-day window", { count: usageCoveredDays }),
    );
  }
  if (usageSource) usageParts.push(usageSource);
  if (usageIsAuthorization) {
    usageParts.push(i18n.t("authorization, not usage"));
  } else if (perUserUsageUnavailable && usageSource) {
    // Nível 2: a contagem é de execução real, então o tile não vira alerta —
    // só registra que sem uso por usuário não dá para ler dormência.
    usageParts.push(i18n.t("no per-user usage, dormancy not measurable"));
  }
  const tiles: TilePayload[] = [
    {
      ovl: i18n.t("USERS"),
      big: fmtInt(extraction.total_users ?? extraction.users_count),
      small: extraction.total_inactive
        ? i18n.t("+ {{count}} inactive (separated)", { count: fmtInt(extraction.total_inactive) })
        : undefined,
      icon: "users",
    },
    {
      ovl: i18n.t("ROLE ASSIGNMENTS"),
      big: fmtInt(extraction.role_assignments_count ?? extraction.role_assignments),
      small: extraction.distinct_roles
        ? i18n.t("{{count}} distinct roles", { count: fmtInt(extraction.distinct_roles) })
        : "AGR_USERS",
      icon: "shield",
    },
    {
      ovl: i18n.t("ROLE-TCODE MAPPINGS"),
      big: fmtInt(extraction.role_tcode_mappings_count ?? extraction.role_tcode_mappings),
      small: "AGR_1251",
      icon: "link",
    },
    {
      ovl: usageIsAuthorization ? i18n.t("AUTHORIZED TRANSACTIONS") : i18n.t("TRANSACTIONS"),
      // Sem medição não há número: o travessão é o mesmo contrato do
      // Diagnóstico (`sectionState.ts`), onde contador de número desconhecido
      // nunca vira `0`. Imprimir `0` com ícone de atividade dizia "medi execução
      // e o resultado foi zero", que é a afirmação que esta issue desfaz.
      big: usageNotMeasured
        ? NO_USAGE_VALUE
        : fmtInt(extraction.transaction_count ?? extraction.transactions),
      small: usageNotMeasured
        ? i18n.t("no usage source responded")
        : usageParts.length > 0
          ? usageParts.join(" · ")
          : undefined,
      icon: usageIsAuthorization || usageNotMeasured ? "alert-triangle" : "activity",
    },
  ];

  return {
    kind: "tiles",
    title: i18n.t("Extraction snapshot"),
    desc: i18n.t("Key volumes pulled from the ERP system in this run"),
    tiles,
  };
}

function buildExtractRfc(extraction: AnyRecord | undefined): RendererPayload | null {
  if (!extraction) return null;
  const entitySets = asRecord(extraction.entity_sets);
  if (!entitySets) return null;
  if (Object.keys(entitySets).length === 0) {
    return emptyResult(
      "kv",
      i18n.t("Raw RFC pulls"),
      i18n.t("Extraction ran and returned no entity set."),
    );
  }
  const rows: KvRow[] = Object.entries(entitySets).map(([k, v]) => ({
    k,
    v: typeof v === "string" || typeof v === "number" ? String(v) : JSON.stringify(v),
    mono: k === "connection" || k === "usage_data_source",
  }));
  return {
    kind: "kv",
    title: i18n.t("Raw RFC pulls · {{count}} entity sets", { count: rows.length }),
    desc: i18n.t("Row counts per entity pulled from the ERP system over RFC"),
    rows,
  };
}

function buildExtractSample(extraction: AnyRecord | undefined): RendererPayload | null {
  if (!extraction) return null;
  const users = asArray(extraction.users) ?? asArray(extraction.user_sample);
  if (!users) return null;
  if (users.length === 0) {
    return emptyResult("table", i18n.t("Sample · USR02"), i18n.t("Extraction ran and returned no user."));
  }
  const sample = users.slice(0, 6) as AnyRecord[];
  const cols = ["BNAME", "USTYP", "CLASS", "GLTGV", "GLTGB", "TRDAT"];
  // `||` e não `??` na cadeia inteira: `??` só cai no próximo termo com
  // null/undefined, e o connector emite string vazia nas cinco colunas de uma
  // vez — o fallback do `BAPI_USER_GETLIST` (rfc.py:2279-2284) grava
  // `user_type`, `user_group`, `last_logon`, `valid_from` e `valid_to` como ""
  // quando a leitura de USR02 falha. Com `??` a linha inteira ficava em
  // branco, e "sem valor" ficava indistinguível de "não extraído".
  const rows = sample.map((u) => [
    asString(u.bname || u.username || u.BNAME),
    asString(u.ustyp || u.USTYP || u.user_type || "—"),
    asString(u.user_group || "—"),
    asString(u.gltgv || u.GLTGV || u.valid_from || "—"),
    asString(u.gltgb || u.GLTGB || u.valid_to || "—"),
    asString(u.trdat || u.TRDAT || u.last_logon || "—"),
  ]);
  const totalUsers = asNumber(extraction.total_users) ?? users.length;
  return {
    kind: "table",
    title: i18n.t("Sample · USR02 (top {{count}} active users)", { count: rows.length }),
    desc: i18n.t("Sample of the extracted ERP user master — full dataset has {{count}} rows", {
      count: formatCount(totalUsers),
    }),
    table: { cols, rows },
  };
}

function buildClassifyPreview(classification: AnyRecord | undefined): RendererPayload | null {
  if (!classification) return null;
  const modulesInScope = asArray(classification.modules_in_scope);
  const customTcodes = asArray(classification.custom_tcodes);
  const tiles: TilePayload[] = [
    {
      ovl: i18n.t("ACTIVE USERS"),
      big: fmtInt(classification.total_active),
      small: i18n.t("included in BOM"),
      icon: "user-check",
    },
    {
      ovl: i18n.t("INACTIVE"),
      big: fmtInt(classification.total_inactive),
      small: i18n.t("separated · excluded"),
      icon: "user-x",
    },
    {
      ovl: i18n.t("MODULES DETECTED"),
      big: modulesInScope ? String(modulesInScope.length) : "—",
      small: modulesInScope ? modulesInScope.join(" · ") : undefined,
      icon: "layers",
    },
    {
      ovl: i18n.t("UNCLASSIFIED Z/Y"),
      big: customTcodes ? String(customTcodes.length) : "—",
      small: i18n.t("no module attributed"),
      icon: "code",
    },
  ];
  return {
    kind: "tiles",
    title: i18n.t("Frontend preview · what the UI shows after Node {{num}}", { num: "02" }),
    desc: i18n.t("Counts streamed after {{fn}} completes", { fn: "classify_modules" }),
    tiles,
  };
}

function buildClassifyUsers(classification: AnyRecord | undefined): RendererPayload | null {
  if (!classification) return null;
  const users = asArray(classification.user_classifications) as AnyRecord[] | undefined;
  if (!users) return null;
  if (users.length === 0) {
    return emptyResult("table", "user_classifications", i18n.t("No users classified in this run."));
  }
  const sample = users.slice(0, 25);
  const cols = [i18n.t("Name"), i18n.t("Roles"), "TCodes", i18n.t("Modules"), i18n.t("Custom Z/Y")];
  const rows = sample.map((u) => {
    const tcodes = asArray(u.tcodes) ?? [];
    const modules = asArray(u.modules) ?? [];
    const roles = asArray(u.roles);
    const customTcodes = asArray(u.custom_tcodes) ?? [];
    // Exibe o nome do usuário com fallback para login via `||` (pois `first_name` pode ser string vazia `""`).
    // O login é mantido entre parênteses para servir de identificador sem duplicar colunas.
    const fullName = [asString(u.first_name), asString(u.last_name)].filter(Boolean).join(" ");
    const login = asString(u.username);
    // Evita repetir o login quando o nome do usuário for o próprio login (ex: contas técnicas).
    // Usa case-insensitive porque o SAP salva os dois campos com formatações de texto diferentes.
    const nameIsLogin = fullName.toLowerCase() === login.toLowerCase();
    return [
      // Se o nome for igual ao login, exibe o login.
      // É o código oficial que o consultor usará, por exelmplo em planilhas, diferentemente do nome no ADRP.
      fullName && login && !nameIsLogin
        ? `${fullName} (${login})`
        : login || fullName || "—",
      roles ? String(roles.length) : "—",
      String(tcodes.length),
      modules.join(", ") || "—",
      String(customTcodes.length),
    ];
  });
  return {
    kind: "table",
    title: i18n.t("user_classifications · per active user"),
    desc: i18n.t("{{path}} — sample of {{count}} rows", {
      path: "state.classification.user_classifications",
      count: formatCount(users.length),
    }),
    table: { cols, rows },
  };
}

function buildClassifyModules(classification: AnyRecord | undefined): RendererPayload | null {
  if (!classification) return null;
  const counts = asRecord(classification.user_counts_by_module);
  const fallbackModules = asArray(classification.modules_in_scope);
  if (!counts && !fallbackModules) return null;

  let entries: Array<{ lbl: string; n: number }>;
  if (counts) {
    entries = Object.entries(counts)
      .map(([lbl, v]) => ({ lbl, n: asNumber(v) ?? 0 }))
      .filter((e) => e.n > 0)
      .sort((a, b) => b.n - a.n);
  } else {
    // Derive evenly-distributed counts from user_classifications
    const users = asArray(classification.user_classifications) as AnyRecord[] | undefined;
    if (!users) return null;
    const tally: Record<string, number> = {};
    for (const u of users) {
      for (const m of asArray(u.modules) ?? []) {
        const key = String(m);
        tally[key] = (tally[key] ?? 0) + 1;
      }
    }
    entries = Object.entries(tally)
      .map(([lbl, n]) => ({ lbl, n }))
      .sort((a, b) => b.n - a.n);
  }

  if (entries.length === 0) {
    return emptyResult(
      "tiles+table",
      "modules_in_scope",
      i18n.t("Classification ran and found no module in scope."),
    );
  }
  // Altera o denominador das barras de soma dos módulos para `total_active`.
  // A soma anterior sobrepunha usuários multimódulo (FI + MM); agora reflete a % real da base faturável
  // e alinha a exibição da tela com o relatório Excel do Nó 06.
  const totalActive = asNumber(classification.total_active);
  const denominator = totalActive != null && totalActive > 0 ? totalActive : undefined;
  const bars: BarPayload[] = entries.map((e, i) => ({
    lbl: e.lbl,
    n: formatCount(e.n),
    // Sem `total_active`, `undefined` — não `0`. O renderer imprime o percentual
    // sem condição, então `0` sairia como "0%" ao lado de uma contagem real de
    // usuários, afirmando uma medição que esta run não sustenta. Evita também a
    // soma dos módulos (`reduce`) como fallback, que reintroduziria a distorção.
    pct: denominator ? Number(((e.n / denominator) * 100).toFixed(1)) : undefined,
    color: MODULE_COLORS[e.lbl] ?? DEFAULT_MODULE_COLORS[i % DEFAULT_MODULE_COLORS.length],
  }));
  // Exibe o aviso de soma >100% apenas se houver sobreposição de módulos por usuários.
  // Se a soma for igual a `total_active`, as barras somam 100% exatamente.
  const assignments = entries.reduce((acc, e) => acc + e.n, 0);
  const hasOverlap = denominator != null && assignments > denominator;
  const base = denominator
    ? i18n.t("share of the {{count}} active users touching each module", {
        count: formatCount(denominator),
      })
    : i18n.t("user count per module; share not measured, this run carries no active-user base");
  return {
    kind: "bars",
    title: i18n.t("{{count}} modules in scope", { count: bars.length }),
    desc: hasOverlap
      ? i18n.t(
          "{{path}} — {{base}}; a user in several modules counts in each, so the bars add up to more than 100%",
          { path: "state.classification.modules_in_scope", base },
        )
      : i18n.t("{{path}} — {{base}}", { path: "state.classification.modules_in_scope", base }),
    bars,
  };
}

function buildClassifyCustom(classification: AnyRecord | undefined): RendererPayload | null {
  if (!classification) return null;
  const customTcodes = asArray(classification.custom_tcodes_detail) as AnyRecord[] | undefined;
  if (!customTcodes) return null;
  if (customTcodes.length === 0) {
    return emptyResult(
      "rows",
      i18n.t("Custom Z/Y TCodes"),
      i18n.t("Every Z/Y transaction was classified — none left without a module."),
    );
  }
  const totalActive = asNumber(classification.total_active);
  const rows: RowPayload[] = customTcodes.slice(0, 12).map((c) => {
    const userCount = asNumber(c.user_count);
    return {
      sev: severityForUserShare(userCount, totalActive),
      l: asString(c.tcode ?? c.name),
      r: userCount != null ? i18n.t("{{count}} users", { count: userCount }) : "—",
      // `description` é sempre gravada (`classify.py:798`) e `reason` pertence a
      // `excluded_by_reason` (`:851`), outra estrutura — os dois reads eram
      // mortos. O fallback afirmava "outside Clean Core scope" numa linha que
      // ninguém alcança; o texto novo descreve o payload em vez de prometer
      // etapa de pipeline.
      x: asString(c.description, i18n.t("No description in the run payload.")),
    };
  });
  return {
    kind: "rows",
    title: i18n.t("Custom Z/Y TCodes · {{count}} with no module", { count: customTcodes.length }),
    desc: i18n.t(
      "{{path}} — Z/Y with no catalog entry, no customer mapping and no prefix rule",
      { path: "state.classification.custom_tcodes_detail" },
    ),
    rows,
  };
}

function buildMapPreview(scopeMapping: AnyRecord | undefined): RendererPayload | null {
  if (!scopeMapping) return null;
  const matched = asNumber(scopeMapping.matched_count) ?? asArray(scopeMapping.scope_items)?.length;
  const unmapped = asNumber(scopeMapping.unmapped_count) ?? asArray(scopeMapping.unmapped_modules)?.length;
  // `candidate_count` é a soma bruta das linhas do SQL antes do dedup por
  // `scope_item_id` (map_scope.py:349), não um universo que tenha sido avaliado.
  // O número que vale a pena mostrar é quanto o dedup comeu — e ele não sai de
  // `matched_count`: itens do RAG entram nele (:469) sem nunca terem passado por
  // `candidate_count`, então a subtração encolheria ou inverteria. A conta exata
  // usa `confidence_source_totals.sql` (:589), que conta as linhas SQL que
  // sobreviveram: toda linha contada ou virou item `sql` ou foi descartada como
  // duplicata, sem terceiro destino.
  const candidates = asNumber(scopeMapping.candidate_count);
  const sqlItemsKept = asNumber(asRecord(scopeMapping.confidence_source_totals)?.sql);
  const duplicatesDropped =
    candidates != null && sqlItemsKept != null ? candidates - sqlItemsKept : undefined;
  const tiles: TilePayload[] = [
    {
      ovl: i18n.t("SCOPE ITEMS MATCHED"),
      big: matched != null ? formatCount(matched) : "—",
      // Sem o agregado por fonte não há conta possível, e inventar uma é o
      // defeito que a FUN.7.1 tirou das barras. Zero, porém, é medição: o
      // subtítulo antigo sumia quando a contagem era falsy.
      small:
        duplicatesDropped != null
          // Sem o qualificador "cross-module": o dedup é por `scope_item_id`
          // (map_scope.py:352) e a referência não tem unicidade em
          // (`ecc_module`, `scope_item_id`), então duas linhas do MESMO módulo
          // entram na conta. Medido em `ecc_scope_mappings.jsonl`: 10 dos 17
          // descartes são intra-módulo, e num cliente só de CO são 4 de 4.
          ? i18n.t("{{count}} duplicate reference rows removed", {
              count: formatCount(duplicatesDropped),
            })
          : undefined,
      icon: "check-circle",
      accent: true,
    },
    {
      ovl: i18n.t("UNMAPPED MODULES"),
      big: unmapped != null ? formatCount(unmapped) : "—",
      small: i18n.t("see Unmapped tab"),
      icon: "alert-triangle",
    },
  ];
  return {
    kind: "tiles",
    title: i18n.t("Frontend preview · what the UI shows during Node {{num}}", { num: "03" }),
    desc: i18n.t("Counts streamed live as {{fn}} runs", { fn: "map_scope_items" }),
    tiles,
  };
}

function buildMapItems(scopeMapping: AnyRecord | undefined): RendererPayload | null {
  if (!scopeMapping) return null;
  const items = asArray(scopeMapping.scope_items) as AnyRecord[] | undefined;
  if (!items) return null;
  if (items.length === 0) {
    return emptyResult(
      "table",
      "scope_items",
      i18n.t("No scope items matched the extracted modules."),
    );
  }
  const cols = [
    i18n.t("ID"),
    i18n.t("Name"),
    i18n.t("ERP Module"),
    i18n.t("Functional Area"),
    i18n.t("Confidence"),
    i18n.t("Key TCodes"),
  ];
  const rows = items.slice(0, 25).map((it) => {
    const tcodes = asArray(it.key_tcodes) ?? asArray(it.tcodes) ?? [];
    // Mesma família do `??` da amostra de usuários: o `map_scope` grava
    // `functional_area: item.get("process_area") or ""` (map_scope.py:120) para
    // os itens vindos do RAG, e `??` deixava a célula em branco em vez de "—".
    return [
      asString(it.id || it.scope_id || it.scope_item_id),
      asString(it.name || it.scope_item_name),
      asString(it.ecc_module || it.module || "—"),
      asString(it.functional_area || "—"),
      asString(it.confidence || "—"),
      tcodes.slice(0, 3).join(", "),
    ];
  });
  return {
    kind: "table",
    title: i18n.t("scope_items · {{count}} deduped", { count: items.length }),
    desc: i18n.t("{{path}} — drives waterfall in Node 05", {
      path: "state.scope_mapping.scope_items",
    }),
    table: { cols, rows },
  };
}

// `classification` entra só pela base da severidade: `scope_mapping` não
// carrega `total_active`, e um módulo com 25 usuários numa base de 120 é 20%
// dela sem tradução — o limiar absoluto chamava isso de `info`.
function buildMapUnmapped(
  scopeMapping: AnyRecord | undefined,
  classification: AnyRecord | undefined,
): RendererPayload | null {
  if (!scopeMapping) return null;
  const unmapped = asArray(scopeMapping.unmapped_modules) as AnyRecord[] | undefined;
  if (!unmapped) return null;
  if (unmapped.length === 0) {
    return emptyResult(
      "rows",
      "unmapped_modules",
      i18n.t("Every extracted module mapped to a scope item."),
    );
  }
  const totalActive = asNumber(classification?.total_active);
  const rows: RowPayload[] = unmapped.map((u) => {
    const userCount = asNumber(u.user_count);
    return {
      sev: severityForUserShare(userCount, totalActive),
      l: asString(u.name ?? u.module ?? u.tcode),
      r: userCount != null ? i18n.t("{{count}} users", { count: userCount }) : "—",
      x: asString(u.description ?? u.reason ?? i18n.t("Awaiting Clean Core review.")),
    };
  });
  return {
    kind: "rows",
    title: i18n.t("unmapped_modules · {{count}} with no scope item", { count: unmapped.length }),
    // O cabeçalho não fala das fontes: `UNMAPPED_REASONS` (map_scope.py:83-99) tem
    // três motivos, e dois deles dizem que a busca semântica NÃO rodou. Como cada
    // linha imprime o seu motivo, generalizar aqui punha as duas afirmações na
    // mesma tela — e é o defeito que o backend já tinha corrigido ao trocar a
    // frase única "No SQL or RAG match" pelos três textos.
    desc: i18n.t(
      "{{path}} — no downstream node consumes the list, so it carries no BOM effect; the reason for each module is on its row",
      { path: "state.scope_mapping.unmapped_modules" },
    ),
    rows,
  };
}

function buildResolvePreview(skuResolution: AnyRecord | undefined): RendererPayload | null {
  if (!skuResolution) return null;
  const usersMapped =
    asNumber(skuResolution.total_mapped) ??
    asArray(skuResolution.user_sku_mappings)?.length;
  const skuLines = asNumber(skuResolution.sku_lines_count) ?? asArray(skuResolution.bom_line_items)?.length;
  const ambiguous =
    asNumber(skuResolution.ambiguous_count) ??
    asArray(skuResolution.ambiguous_users)?.length;

  // Regra inativa gera saída sem SKU/BOM (`resolve_skus.py`), ajustando a contagem do tile "all active assigned".
  const unmappedLicense = asNumber(skuResolution.unmapped_license_count);
  const pendingReview = asArray(skuResolution.pending_review_users)?.length;
  // Unifica os estados do gate em `bom_status` para evitar redundância com `requires_review`.
  const bomStatus = skuResolution.bom_status;

  const tiles: TilePayload[] = [
    {
      ovl: i18n.t("USERS MAPPED"),
      big: usersMapped != null ? formatCount(usersMapped) : "—",
      // Valida apenas licença (falta `total_active` do Nó 02); runs antigas retornam `undefined` (trata em `renderers.tsx`).
      small:
        unmappedLicense == null
          ? undefined
          : unmappedLicense > 0
            ? i18n.t("{{count}} without a resolved license", { count: formatCount(unmappedLicense) })
            : i18n.t("all resolved to a license type"),
      icon: "user-check",
      accent: true,
    },
    {
      ovl: i18n.t("SKU LINES"),
      big: skuLines != null ? String(skuLines) : "—",
      small: i18n.t("distinct license types"),
      icon: "list",
    },
    {
      ovl: i18n.t("AMBIGUOUS"),
      big: ambiguous != null ? String(ambiguous) : "—",
      // Oculta o texto quando a contagem é zero **ou ausente**: campo que não veio
      // não é campo zerado, e sem o `== null` a frase saía por cima de um `—`.
      // Reflete `pending_review_users`, que só coincide com os ambíguos antes de haver resposta.
      small:
        pendingReview != null && pendingReview > 0
          ? i18n.t("{{count}} pending human review", { count: formatCount(pendingReview) })
          : ambiguous == null || ambiguous === 0
            ? undefined
            : i18n.t("flagged · no pending review"),
      icon: "alert-triangle",
    },
    {
      ovl: i18n.t("BOM PREVIEW"),
      // `Ready` saía sempre que houvesse qualquer linha, inclusive com o BOM
      // reprovado pelo próprio backend. Sem o campo, a contagem é o que a run
      // sustenta — e ela não opina sobre o gate.
      big:
        bomStatus === "approved"
          ? i18n.t("Ready")
          : bomStatus === "needs_review"
            ? i18n.t("Needs review")
            : bomStatus === "failed"
              ? i18n.t("Failed")
              : skuLines != null
                ? i18n.t("{{count}} lines", { count: skuLines })
                : "—",
      // Cálculo incondicional (`max(...)`), mas incondicional sobre um cálculo que
      // rodou: sem contagem de linhas ele não rodou, e a frase sozinha afirmaria o
      // que a run não mediu. Anda junto com o número que a sustenta.
      small:
        skuLines != null
          ? i18n.t("{{count}} SKU lines · with min_blocks applied", { count: skuLines })
          : undefined,
      icon: "file-text",
    },
  ];
  return {
    kind: "tiles",
    title: i18n.t("Frontend preview · what the UI shows after Node {{num}}", { num: "05" }),
    desc: i18n.t("Counts streamed after {{fn}} completes", { fn: "resolve_skus" }),
    tiles,
  };
}

function buildResolveUsers(skuResolution: AnyRecord | undefined): RendererPayload | null {
  if (!skuResolution) return null;
  const users = asArray(skuResolution.user_sku_mappings) as AnyRecord[] | undefined;
  if (!users) return null;
  if (users.length === 0) {
    return emptyResult(
      "table",
      "user_sku_mappings",
      i18n.t("No user mapped to a SKU in this run."),
    );
  }
  // `(codes)` no cabeçalho porque `license_types_considered` é `sorted(set)` de
  // inteiros (resolve_skus.py:952) e a célula sai "3 · 7", ao lado de uma coluna
  // `License` que mostra o nome. Traduzir código → nome exigiria juntar
  // `license_type_distribution` com `candidate_license_types`, e o mapa resultante
  // é parcial: código avaliado que ninguém recebeu não aparece em nenhum dos dois.
  // Célula meio nome, meio número engana mais do que código puro anunciado.
  const cols = [
    i18n.t("User"),
    i18n.t("Modules"),
    i18n.t("License"),
    "SKU",
    i18n.t("Considered (codes)"),
    i18n.t("Why"),
    i18n.t("Ambiguous"),
  ];
  const rows = users.slice(0, 25).map((u) => {
    const modules = asArray(u.modules) ?? [];
    // `mapping_chain` é literal incondicional no backend (resolve_skus.py:929),
    // mas run antiga não o tem — e `UserMappingsTab` já carrega um teste do
    // caminho legado. Leitura opcional, travessão quando falta.
    const chain = asRecord(u.mapping_chain);
    const considered = asArray(chain?.license_types_considered) ?? [];
    // `ambiguity_reason` é `""` para quem não é ambíguo (:865-870), e aí quem
    // responde "por quê" é como o empate foi resolvido.
    const why =
      asString(chain?.ambiguity_reason) || asString(chain?.ambiguity_resolution) || "—";
    return [
      asString(u.username ?? u.bname),
      modules.join(", ") || "—",
      asString(u.license_type_name ?? "—"),
      asString(u.sku_number ?? "—"),
      considered.join(" · ") || "—",
      why,
      u.is_ambiguous ? i18n.t("yes") : "—",
    ];
  });
  return {
    kind: "table",
    title: i18n.t("user_sku_mappings · per-user audit"),
    // A desc prometia a cadeia inteira e a tabela não mostrava nenhuma parte
    // dela. Das 12 chaves de `mapping_chain`, duas respondem "por que este
    // usuário custa isto" — as mesmas que o backend preserva ao desidentificar
    // o payload para o diagnóstico (diagnostic_builder.py:1244-1266). O texto
    // passa a nomear o que está na tela.
    desc: i18n.t(
      "{{path}} — license_types_considered and the ambiguity reason from mapping_chain",
      { path: "state.sku_resolution.user_sku_mappings" },
    ),
    table: { cols, rows },
  };
}

function buildResolveBom(skuResolution: AnyRecord | undefined): RendererPayload | null {
  if (!skuResolution) return null;
  const lines = asArray(skuResolution.bom_line_items) as AnyRecord[] | undefined;
  if (!lines) return null;
  if (lines.length === 0) {
    return emptyResult(
      "table",
      "bom_line_items",
      i18n.t("No BOM line produced — no user resolved to a billable SKU."),
    );
  }
  const cols = [
    "SKU",
    i18n.t("Description"),
    i18n.t("Users"),
    i18n.t("Effective"),
    i18n.t("Unit BRL/mo"),
    i18n.t("Annual BRL"),
  ];
  const rows = lines.map((l) => [
    asString(l.sku_number),
    asString(l.description),
    fmtInt(l.user_count),
    fmtInt(l.effective_count),
    fmtBrl(l.unit_price_brl),
    fmtBrl(l.annual_total),
  ]);
  return {
    kind: "table",
    title: i18n.t("bom_line_items · per-SKU rollup"),
    desc: i18n.t("{{path}} — feeds Node 06 generator", { path: "state.sku_resolution.bom_line_items" }),
    table: { cols, rows },
  };
}

function buildResolveAmbiguous(skuResolution: AnyRecord | undefined): RendererPayload | null {
  if (!skuResolution) return null;
  const pending = asArray(skuResolution.pending_review_users) as AnyRecord[] | undefined;
  if (pending && pending.length > 0) {
    const rows: RowPayload[] = pending.slice(0, 12).map((p) => {
      const candidates = asArray(p.candidate_license_types) as AnyRecord[] | undefined;
      const candidateNames = (candidates ?? [])
        .map((c) => asString(c.license_type_name ?? c.license_type_code))
        .filter(Boolean)
        .join(" · ");
      const tentative = asString(
        p.tentative_license_type_name ?? p.tentative_license_type_code ?? i18n.t("tentative")
      );
      // Exibe o motivo real na coluna em vez do texto genérico.
      const reason = asString(p.reason, i18n.t("No ambiguity reason in the run payload."));
      return {
        sev: "warn",
        l: asString(p.username ?? "—"),
        r: tentative,
        x: candidateNames
          ? i18n.t("{{reason}} · candidates: {{candidates}}", { reason, candidates: candidateNames })
          : reason,
      };
    });
    return {
      kind: "rows",
      title: i18n.t("pending_review_users · {{count}} awaiting confirmation", { count: pending.length }),
      // A aba é apenas leitura. O override real ocorre via textarea livre no HumanInputPanel.tsx.
      // Atualiza a descrição para refletir o que a aba exibe e onde a decisão é tomada.
      desc: i18n.t(
        "{{path}} — read-only list of the tentative license and the candidates considered; the decision itself is typed into the pause panel",
        { path: "state.sku_resolution.pending_review_users" },
      ),
      rows,
    };
  }

  const ambiguous = asArray(skuResolution.ambiguous_users);
  if (!ambiguous) return null;
  if (ambiguous.length === 0) {
    return emptyResult(
      "rows",
      "ambiguous_users",
      i18n.t("No ambiguous users — every mapping resolved to a single license type."),
    );
  }
  const rows: RowPayload[] = ambiguous.slice(0, 12).map((entry) => {
    if (typeof entry === "string") {
      return {
        sev: "warn",
        l: entry,
        r: i18n.t("ambiguous"),
        x: i18n.t("is_ambiguous = true · awaiting human review."),
      };
    }
    const o = asRecord(entry) ?? {};
    return {
      sev: "warn",
      l: asString(o.username ?? o.user ?? "—"),
      r: asString(o.license_type_name ?? i18n.t("ambiguous")),
      x: asString(o.reason, i18n.t("No ambiguity reason in the run payload.")),
    };
  });
  return {
    kind: "rows",
    title: i18n.t("ambiguous_users · {{count}} awaiting confirmation", { count: ambiguous.length }),
    // `ambiguous_users` contém apenas usernames (`List[str]`); candidatos são exclusivos de `pending_review_users`.
    desc: i18n.t(
      "{{path}} — this run carries usernames only, with no tentative license or candidate list to show",
      { path: "state.sku_resolution.ambiguous_users" },
    ),
    rows,
  };
}

function buildBomLines(bom: AnyRecord | undefined): RendererPayload | null {
  if (!bom) return null;
  const lines = asArray(bom.bom_summary) as AnyRecord[] | undefined;
  if (!lines) return null;
  if (lines.length === 0) {
    return emptyResult("table", "bom_summary", i18n.t("BOM generated with no billable line."));
  }
  const cols = [
    "SKU",
    i18n.t("Description"),
    i18n.t("Effective Qty"),
    i18n.t("Unit BRL/mo"),
    i18n.t("Monthly BRL"),
    i18n.t("Annual BRL"),
  ];
  const rows = lines.map((l) => [
    asString(l.sku_number),
    asString(l.description),
    fmtInt(l.effective_count),
    fmtBrl(l.unit_price_brl),
    fmtBrl(l.monthly_total),
    fmtBrl(l.annual_total),
  ]);
  const totalUsers = bom.total_users_in_bom;
  const monthly = bom.monthly_grand_total;
  const annual = bom.annual_grand_total;
  const footer = [
    "",
    i18n.t("Grand totals"),
    fmtInt(totalUsers),
    "",
    fmtBrl(monthly),
    fmtBrl(annual),
  ];
  const unpriced = unpricedSkus(bom);
  return {
    kind: "note+table",
    title: i18n.t("bom_summary · final line items"),
    desc: i18n.t("{{path}} — no token emitted, file is the deliverable", {
      path: "state.bom.bom_summary",
    }),
    // O total soma só as linhas com preço, e o backend já conta quantas ficaram
    // de fora (`unpriced_count`). Sem esta nota a tela afirma um total que
    // subestima o custo, e quem lê não tem como saber (o XLSX avisa, ela não).
    note: unpriced
      ? i18n.t(
          "The totals exclude {{count}} line(s) with no unit price: {{skus}}. The real cost is higher.",
          { count: unpriced.count, skus: unpriced.skus.join(", ") },
        )
      : undefined,
    table: { cols, rows, footer },
  };
}

/**
 * As linhas sem preço unitário da run, ou ``null`` quando não há nenhuma.
 *
 * ``unpriced_count`` ausente é "não medi" e devolve ``null``: run antiga não
 * ganha aviso inventado. Os SKUs saem das próprias linhas, porque o backend
 * grava a contagem mas não a lista.
 */
function unpricedSkus(
  bom: AnyRecord,
): { count: number; skus: string[] } | null {
  const count = asNumber(bom.unpriced_count);
  if (count == null || count <= 0) return null;
  const skus = ((asArray(bom.bom_summary) as AnyRecord[] | undefined) ?? [])
    .filter((l) => asNumber(l.unit_price_brl) == null)
    .map((l) => asString(l.sku_number))
    .filter(Boolean);
  return { count, skus };
}

function buildBomTotals(bom: AnyRecord | undefined): RendererPayload | null {
  if (!bom) return null;
  const monthly = asNumber(bom.monthly_grand_total);
  const annual = asNumber(bom.annual_grand_total);
  const totalUsers = asNumber(bom.total_users_in_bom);
  const skuLines = asNumber(bom.total_sku_lines) ?? asArray(bom.bom_summary)?.length;
  if (monthly == null && annual == null && totalUsers == null && skuLines == null) return null;
  const unpriced = unpricedSkus(bom);
  const tiles: TilePayload[] = [
    {
      ovl: i18n.t("MONTHLY"),
      big: fmtBrlCompact(monthly),
      small: unpriced
        ? i18n.t("excludes {{count}} line(s) with no unit price", {
            count: unpriced.count,
          })
        : i18n.t("sum of SKU lines"),
      icon: "calendar",
    },
    {
      ovl: i18n.t("ANNUAL"),
      big: fmtBrlCompact(annual),
      small: i18n.t("12 × monthly · no ramp"),
      icon: "sigma",
      accent: true,
    },
    {
      ovl: i18n.t("TOTAL USERS"),
      big: totalUsers != null ? formatCount(totalUsers) : "—",
      small: i18n.t("effective (max blocks/headcount)"),
      icon: "users",
    },
    {
      ovl: i18n.t("SKU LINES"),
      big: skuLines != null ? String(skuLines) : "—",
      small: i18n.t("distinct line items"),
      icon: "list",
    },
  ];
  return {
    kind: "tiles",
    title: i18n.t("Grand totals · BRL"),
    desc: i18n.t("monthly_grand_total + annual_grand_total · live =SUM in Excel"),
    tiles,
  };
}

function buildBomExcel(bom: AnyRecord | undefined): RendererPayload | null {
  if (!bom?.bom_file_path) return null;
  const path = asString(bom.bom_file_path);
  const rows: KvRow[] = [
    {
      k: i18n.t("Tab 1 · BOM Summary"),
      v: i18n.t("Live =MAX(users, min_blocks) and =SUM/=F*G formulas — recalc on edit"),
    },
    {
      k: i18n.t("Tab 2 · User Detail"),
      v: i18n.t("Per-user BNAME · modules · scope_items · capabilities · license · SKU"),
    },
    {
      k: i18n.t("Tab 3 · Module Usage + Scope"),
      v: i18n.t("Modules + scope items with confidence and key TCodes"),
    },
    {
      k: i18n.t("Tab 4 · Inactive Users"),
      v: i18n.t("Separated rows from BOM — for audit only"),
    },
    {
      k: i18n.t("Tab 5 · Master Data"),
      v: i18n.t("KNA1 · LFA1 · MARA · SKA1 · CSKS · BUT000 + CVI status snapshot"),
    },
    {
      k: i18n.t("Tab 6 · Custom Code"),
      v: i18n.t("Z/Y TCodes inventory · Clean Core risk score"),
    },
    {
      k: i18n.t("Tab 7 · Integrations"),
      v: i18n.t("SM59 RFC destinations + IDoc inbound/outbound 30-day stats"),
    },
    { k: i18n.t("File"), v: path, mono: true },
  ];
  return {
    kind: "kv",
    title: i18n.t("BOM Excel · 7 tabs"),
    desc: i18n.t("{{path}} → MinIO bucket runs/<run_id>/bom/", { path: "state.bom.bom_file_path" }),
    rows,
  };
}

/**
 * O título do relatório, pela procedência que a execução registrou (#1043).
 *
 * O nó 06 grava `metrics.report_source`: `"llm"` quando o modelo respondeu,
 * `"template"` quando caiu no texto determinístico. Em 03/10/2026 o token do
 * SSO expirou, a entrega caiu no template, e a aba continuou dizendo "via
 * Bedrock Claude" sobre um texto que o Bedrock não escreveu — com 4.220
 * caracteres contra os 21.601 da mesma entrega com o modelo.
 *
 * Sem a marca, não se credita ninguém: execução gravada antes do campo existir
 * não autoriza afirmar procedência.
 */
function reportProvenance(metrics: AnyRecord | undefined): {
  title: string;
  desc: string;
} {
  const source = asString(metrics?.report_source);
  if (source === "llm") {
    return {
      title: i18n.t("mapping_report · Markdown via Bedrock Claude"),
      desc: i18n.t("LLM-generated with deterministic fallback template if LLM fails"),
    };
  }
  if (source === "template") {
    return {
      title: i18n.t("mapping_report · deterministic template"),
      desc: i18n.t(
        "The model did not answer in this run, and the static text took its place.",
      ),
    };
  }
  return {
    title: i18n.t("mapping_report · Markdown"),
    desc: i18n.t("This run did not record which path produced the report."),
  };
}

function buildReportReport(outputs: AnyRecord | undefined): RendererPayload | null {
  if (!outputs) return null;
  const md = asString(outputs.mapping_report);
  if (!md) return null;
  const metrics = outputs.metrics as AnyRecord | undefined;
  return {
    kind: "markdown",
    ...reportProvenance(metrics),
    md,
  };
}

function buildReportMetrics(outputs: AnyRecord | undefined): RendererPayload | null {
  if (!outputs) return null;
  const metrics = asRecord(outputs.metrics);
  const classification = asRecord(outputs.classification_summary);
  const skuResolution = asRecord(outputs.sku_resolution);
  const scopeMapping = asRecord(outputs.scope_mapping);
  const bom = asRecord(outputs.bom_summary);
  if (!metrics && !classification && !skuResolution && !scopeMapping && !bom) return null;
  const tiles: TilePayload[] = [
    {
      ovl: i18n.t("TOTAL ACTIVE"),
      big: fmtInt(metrics?.total_active_users ?? classification?.total_active),
      small: i18n.t("users in BOM"),
      icon: "user-check",
    },
    {
      ovl: i18n.t("TOTAL INACTIVE"),
      big: fmtInt(metrics?.total_inactive_users ?? classification?.total_inactive),
      small: i18n.t("separated · Tab 4"),
      icon: "user-x",
    },
    {
      ovl: i18n.t("MODULES"),
      big:
        metrics?.modules_count != null
          ? String(metrics.modules_count)
          : classification?.modules_in_scope
            ? String((asArray(classification.modules_in_scope) ?? []).length)
            : "—",
      small: i18n.t("in scope"),
      icon: "layers",
    },
    {
      ovl: i18n.t("SCOPE ITEMS"),
      big:
        metrics?.scope_items_count != null
          ? String(metrics.scope_items_count)
          : scopeMapping?.scope_items
            ? String((asArray(scopeMapping.scope_items) ?? []).length)
            : "—",
      small: i18n.t("matched"),
      icon: "check-circle",
    },
    {
      ovl: i18n.t("SKU LINES"),
      big:
        metrics?.sku_lines != null
          ? String(metrics.sku_lines)
          : bom?.total_sku_lines != null
            ? String(bom.total_sku_lines)
            : "—",
      small: i18n.t("distinct"),
      icon: "list",
    },
    {
      ovl: i18n.t("AMBIGUOUS"),
      big:
        metrics?.ambiguous_users != null
          ? String(metrics.ambiguous_users)
          : skuResolution?.ambiguous_users
            ? String((asArray(skuResolution.ambiguous_users) ?? []).length)
            : "—",
      small: i18n.t("awaiting confirm"),
      icon: "alert-triangle",
    },
    {
      ovl: i18n.t("ANNUAL BRL"),
      big: fmtBrlCompact(metrics?.annual_total_brl ?? bom?.annual_grand_total),
      small: i18n.t("grand total"),
      icon: "sigma",
      accent: true,
    },
  ];
  return {
    kind: "tiles",
    title: i18n.t("metrics · embedded in outputs"),
    desc: i18n.t("Final consolidated package — outputs.metrics"),
    tiles,
  };
}

function buildReportAudit(outputs: AnyRecord | undefined): RendererPayload | null {
  if (!outputs) return null;
  const rows: KvRow[] = [];
  const extraction = asRecord(outputs.extraction_summary);
  const classification = asRecord(outputs.classification_summary);
  const scopeMapping = asRecord(outputs.scope_mapping);
  const skuResolution = asRecord(outputs.sku_resolution);
  const bom = asRecord(outputs.bom_summary);
  if (extraction) {
    rows.push({
      k: "extraction_summary",
      v: i18n.t("{{users}} users · {{roles}} role assignments", {
        users: fmtInt(extraction.total_users),
        roles: fmtInt(extraction.role_assignments_count),
      }),
    });
  }
  if (classification) {
    const modulesLen = (asArray(classification.modules_in_scope) ?? []).length;
    rows.push({
      k: "classification_summary",
      v: i18n.t("{{active}} active · {{modules}} modules in scope", {
        active: fmtInt(classification.total_active),
        modules: modulesLen,
      }),
    });
  }
  if (scopeMapping) {
    const scopeLen = (asArray(scopeMapping.scope_items) ?? []).length;
    const unmappedLen = (asArray(scopeMapping.unmapped_modules) ?? []).length;
    rows.push({
      k: "scope_mapping",
      v: i18n.t("{{scope}} scope_items · {{unmapped}} unmapped_modules", {
        scope: scopeLen,
        unmapped: unmappedLen,
      }),
    });
  }
  if (skuResolution) {
    const userLen = (asArray(skuResolution.user_sku_mappings) ?? []).length;
    const lineLen = (asArray(skuResolution.bom_line_items) ?? []).length;
    rows.push({
      k: "sku_resolution",
      v: i18n.t("{{users}} user_sku_mappings · {{lines}} bom_line_items", {
        users: formatCount(userLen),
        lines: lineLen,
      }),
    });
  }
  if (bom) {
    rows.push({
      k: "bom_summary",
      v: i18n.t("Embedded copy of BOM lines (single source of truth)"),
    });
    if (bom.bom_file_path) {
      rows.push({ k: "bom_file", v: asString(bom.bom_file_path), mono: true });
    }
  }
  if (rows.length === 0) return null;
  return {
    kind: "kv",
    title: i18n.t("outputs · full audit copies of upstream state"),
    desc: i18n.t("Self-contained package — replayable without reconnecting to ERP"),
    rows,
  };
}

function buildReportFsm(execution: AnyRecord | undefined): RendererPayload | null {
  if (!execution) return null;
  const rows: KvRow[] = [];
  if (execution.phase) rows.push({ k: "current_phase", v: asString(execution.phase), mono: true });
  if (execution.completed_at)
    rows.push({ k: "completed_at", v: asString(execution.completed_at), mono: true });
  if (execution.tokens_used != null)
    rows.push({ k: "tokens_used", v: asString(execution.tokens_used) });
  if (execution.cost_estimate != null)
    rows.push({ k: "cost_estimate", v: asString(execution.cost_estimate) });
  if (execution.duration) rows.push({ k: "duration", v: asString(execution.duration) });
  if (execution.status) rows.push({ k: "status", v: asString(execution.status) });
  if (rows.length === 0) return null;
  return {
    kind: "kv",
    title: i18n.t("FSM closure · run committed"),
    desc: i18n.t("{{fn}} closes the finite state machine", { fn: "generate_report" }),
    rows,
  };
}

// ── Public API ─────────────────────────────────────────────────────────────

export interface NodeDataCtx {
  outputs: AnyRecord | null | undefined;
  metrics?: AnyRecord | null;
}

export type NodeDataMap = Record<string, RendererPayload>;

export function buildNodeData(ctx: NodeDataCtx): NodeDataMap {
  const outputs = ctx.outputs ?? undefined;
  const extraction = asRecord(outputs?.extraction_summary);
  const classification = asRecord(outputs?.classification_summary);
  const scopeMapping = asRecord(outputs?.scope_mapping);
  const skuResolution = asRecord(outputs?.sku_resolution);
  const bom = asRecord(outputs?.bom_summary);
  const fsmExecution = ctx.metrics ?? undefined;

  const out: NodeDataMap = {};
  const set = (key: string, payload: RendererPayload | null, fallback: RendererPayload) => {
    out[key] = payload ?? fallback;
  };

  // Template único do "ainda não rodou", consolidado — as 23 chamadas abaixo
  // citavam a mesma frase com o número do nó e o nome da função trocados.
  const awaiting = (num: string, fn: string) =>
    i18n.t("Awaiting Node {{num}} output ({{fn}})", { num, fn });

  // 01 EXTRACT
  set(
    "extract.preview",
    buildExtractPreview(extraction),
    placeholder(
      "tiles",
      i18n.t("Frontend preview · what the UI shows after Node {{num}}", { num: "01" }),
      awaiting("01", "extract_erp_data"),
    ),
  );
  set(
    "extract.rfc",
    buildExtractRfc(extraction),
    placeholder("kv", i18n.t("Raw RFC pulls"), awaiting("01", "extract_erp_data")),
  );
  set(
    "extract.sample",
    buildExtractSample(extraction),
    placeholder("table", i18n.t("Sample · USR02"), awaiting("01", "extract_erp_data")),
  );

  // 02 CLASSIFY
  set(
    "classify.preview",
    buildClassifyPreview(classification),
    placeholder(
      "tiles",
      i18n.t("Frontend preview · what the UI shows after Node {{num}}", { num: "02" }),
      awaiting("02", "classify_modules"),
    ),
  );
  set(
    "classify.users",
    buildClassifyUsers(classification),
    placeholder("table", "user_classifications", awaiting("02", "classify_modules")),
  );
  set(
    "classify.modules",
    buildClassifyModules(classification),
    placeholder("bars", i18n.t("Modules in scope"), awaiting("02", "classify_modules")),
  );
  set(
    "classify.custom",
    buildClassifyCustom(classification),
    placeholder("rows", i18n.t("Custom Z/Y TCodes"), awaiting("02", "classify_modules")),
  );

  // 03 MAP
  set(
    "map.preview",
    buildMapPreview(scopeMapping),
    placeholder(
      "tiles",
      i18n.t("Frontend preview · what the UI shows during Node {{num}}", { num: "03" }),
      awaiting("03", "map_scope_items"),
    ),
  );
  set(
    "map.items",
    buildMapItems(scopeMapping),
    placeholder("table", "scope_items", awaiting("03", "map_scope_items")),
  );
  set(
    "map.unmapped",
    buildMapUnmapped(scopeMapping, classification),
    placeholder("rows", "unmapped_modules", awaiting("03", "map_scope_items")),
  );

  // 04 RESOLVE
  set(
    "resolve.preview",
    buildResolvePreview(skuResolution),
    placeholder(
      "tiles",
      i18n.t("Frontend preview · what the UI shows after Node {{num}}", { num: "05" }),
      awaiting("05", "resolve_skus"),
    ),
  );
  set(
    "resolve.users",
    buildResolveUsers(skuResolution),
    placeholder("table", "user_sku_mappings", awaiting("05", "resolve_skus")),
  );
  set(
    "resolve.bom",
    buildResolveBom(skuResolution),
    placeholder("table", "bom_line_items", awaiting("05", "resolve_skus")),
  );
  set(
    "resolve.amb",
    buildResolveAmbiguous(skuResolution),
    placeholder("rows", "ambiguous_users", awaiting("05", "resolve_skus")),
  );

  // 05 BOM
  set(
    "bom.lines",
    buildBomLines(bom),
    placeholder("note+table", "bom_summary", awaiting("06", "generate_bom")),
  );
  set(
    "bom.totals",
    buildBomTotals(bom),
    placeholder("tiles", i18n.t("Grand totals · BRL"), awaiting("06", "generate_bom")),
  );
  set(
    "bom.excel",
    buildBomExcel(bom),
    placeholder("kv", i18n.t("BOM Excel · 7 tabs"), awaiting("06", "generate_bom")),
  );

  // 06 REPORT
  set(
    "report.report",
    buildReportReport(outputs),
    placeholder(
      "markdown",
      i18n.t("mapping_report · Markdown via Bedrock Claude"),
      awaiting("07", "generate_report"),
    ),
  );
  set(
    "report.metrics",
    buildReportMetrics(outputs),
    placeholder("tiles", "metrics", awaiting("07", "generate_report")),
  );
  set(
    "report.audit",
    buildReportAudit(outputs),
    placeholder("kv", "outputs", awaiting("07", "generate_report")),
  );
  set(
    "report.fsm",
    buildReportFsm(fsmExecution),
    placeholder("kv", i18n.t("FSM closure"), awaiting("07", "generate_report")),
  );

  return out;
}

// Agregado server-side (veredito efetivo) usado pra sobrescrever os contadores
// da activation — reflete edições do analista + aprovação, não só o payload.
export interface ActivationSummaryCounts {
  total: number;
  counts: { activate: number; decision: number; out_of_scope: number };
}

export function tabCountFor(
  node: NodeSpec,
  outputs: AnyRecord | null | undefined,
  countryOnly = true,
  summary?: ActivationSummaryCounts | null,
): Record<string, string | number | undefined> {
  const counts: Record<string, string | number | undefined> = {};
  for (const tab of node.tabs) counts[tab.id] = undefined;

  switch (node.id) {
    case "extract": {
      const extraction = asRecord(outputs?.extraction_summary);
      if (!extraction) return counts;
      // O badge conta os tiles que o builder devolve, nunca uma literal.
      // A literal `4` economizava uma construção por render de aba, mas o
      // teste que ficava como guarda dela só pega tile incondicional: um tile
      // que aparecesse apenas quando `extraction` tem certa chave passaria
      // verde no teste e mentiria na tela. Quatro objetos pequenos por render
      // é barato; o buraco não era.
      const preview = buildExtractPreview(extraction);
      if (preview?.kind === "tiles") counts.preview = preview.tiles.length;
      const entitySets = asRecord(extraction.entity_sets);
      if (entitySets) counts.rfc = Object.keys(entitySets).length;
      const totalUsers = asNumber(extraction.total_users);
      if (totalUsers != null) counts.sample = formatCount(totalUsers);
      return counts;
    }
    case "classify": {
      const classification = asRecord(outputs?.classification_summary);
      if (!classification) return counts;
      counts.preview = 4;
      const users = asArray(classification.user_classifications);
      if (users) counts.users = formatCount(users.length);
      const modules = asArray(classification.modules_in_scope);
      if (modules) counts.modules = modules.length;
      // Só `custom_tcodes_detail`, sem cair em `custom_tcodes`: o conteúdo da
      // aba lê exclusivamente o detail (`buildClassifyCustom`), e os dois campos
      // têm formatos diferentes — `custom_tcodes` é lista de strings
      // (`classify.py:812`), o detail é lista de objetos (`:804`). Com o
      // fallback, uma run sem o detail mostrava número no badge e placeholder no
      // conteúdo. Badge e aba respondem pela mesma fonte.
      const custom = asArray(classification.custom_tcodes_detail);
      if (custom) counts.custom = custom.length;
      return counts;
    }
    case "map": {
      const scopeMapping = asRecord(outputs?.scope_mapping);
      if (!scopeMapping) return counts;
      counts.preview = 2;
      const items = asArray(scopeMapping.scope_items);
      if (items) counts.items = items.length;
      const unmapped = asArray(scopeMapping.unmapped_modules);
      if (unmapped) counts.unmapped = unmapped.length;
      return counts;
    }
    case "activation": {
      // Preferimos o agregado server-side (veredito EFETIVO): reflete as edições
      // do analista + aprovação e bate com o Overview/Consolidated.
      if (summary) {
        counts.overview = summary.total;
        counts.catalog = summary.total;
        counts.decisions = summary.counts.decision;
        counts.consolidated = summary.counts.activate;
        return counts;
      }
      const scopeActivation = asRecord(outputs?.scope_activation);
      // Fallback (sem agregado): eixo Sugestão do payload + filtro de país.
      const items = (asArray(scopeActivation?.items) ?? []) as AnyRecord[];
      if (items.length) {
        const outByCountry = new Set([
          "Not available in country",
          "Not in Availability",
        ]);
        const base = countryOnly
          ? items.filter(
              (i) => !outByCountry.has(String(i.suggestion_reason ?? "")),
            )
          : items;
        counts.overview = base.length;
        counts.catalog = base.length;
        counts.decisions = base.filter(
          (i) => i.suggestion === "decision",
        ).length;
        // Consolidado ~ o que será ativado (aprox. pela sugestão; effective
        // ajusta com as edições do analista).
        counts.consolidated = base.filter(
          (i) => i.suggestion === "activate",
        ).length;
      } else {
        // Fallback p/ runs antigas sem `suggestion`: usa o summary ECC.
        const summary = asRecord(scopeActivation?.summary);
        if (summary) {
          const total = asNumber(summary.total);
          counts.overview = total;
          counts.catalog = total;
          counts.decisions =
            (asNumber(summary.needs_decision) ?? 0) +
            (asNumber(summary.needs_review) ?? 0);
        }
      }
      return counts;
    }
    case "resolve": {
      const skuResolution = asRecord(outputs?.sku_resolution);
      if (!skuResolution) return counts;
      counts.preview = 4;
      const mappings = asArray(skuResolution.user_sku_mappings);
      if (mappings) counts.users = formatCount(mappings.length);
      const lines = asArray(skuResolution.bom_line_items);
      if (lines) counts.bom = lines.length;
      const amb = asArray(skuResolution.ambiguous_users);
      if (amb) counts.amb = amb.length;
      return counts;
    }
    case "bom": {
      const bom = asRecord(outputs?.bom_summary);
      if (!bom) return counts;
      const lines = asArray(bom.bom_summary);
      if (lines) counts.lines = lines.length;
      counts.totals = bom.annual_grand_total ? fmtBrlCompact(bom.annual_grand_total) : undefined;
      counts.excel = bom.bom_file_path ? 7 : undefined;
      return counts;
    }
    case "report": {
      counts.report = outputs?.mapping_report ? "MD" : undefined;
      counts.metrics = outputs ? Object.keys(outputs).length : undefined;
      counts.audit = outputs ? Object.keys(outputs).length : undefined;
      counts.fsm = "✓";
      return counts;
    }
    default:
      return counts;
  }
}

export function tabsWithCounts(
  node: NodeSpec,
  outputs: AnyRecord | null | undefined,
  countryOnly = true,
  summary?: ActivationSummaryCounts | null,
) {
  const counts = tabCountFor(node, outputs, countryOnly, summary);
  return node.tabs.map((t) => ({ ...t, count: counts[t.id] ?? t.count ?? "—" }));
}

export function nodeIdsInOrder(): NodeId[] {
  return NODES.map((n) => n.id);
}
